'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';

/**
 * Visual + behavioural props common to all NavItem renders. Element-specific
 * props (e.g. `href`, `to`, `onClick`) are inferred from the chosen `as` type.
 */
export interface NavItemOwnProps {
  /** Leading icon node, always visible including in collapsed mode. */
  icon: React.ReactNode;
  /** Nav item label text. Required (used for accessibility even when collapsed). */
  label: string;
  /** Marks the item as the current page. Applies accent styles and `aria-current="page"`. */
  active?: boolean;
  /** Short count or label displayed as a trailing pill. */
  badge?: string;
  /** Color scheme for the badge. */
  badgeTone?: 'default' | 'error';
  /** Hides label and badge; renders icon-only layout. */
  collapsed?: boolean;
}

/**
 * Polymorphic NavItem props. `E` is the element/component to render as
 * (default `'a'`). All native props of `E` flow through.
 */
export type NavItemProps<E extends React.ElementType = 'a'> = NavItemOwnProps & {
  /** DOM element or component to render. Defaults to `'a'`. */
  as?: E;
} & Omit<React.ComponentPropsWithoutRef<E>, keyof NavItemOwnProps | 'as'>;

const Root = styled.a<{ $active: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  width: 100%;
  padding: ${(p) => (p.$collapsed ? '8px 0' : '7px 16px')};
  margin: 1px 8px;
  border: none;
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => (p.$active ? p.theme.colors.brand.primarySoft : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.brand.primary : p.theme.colors.neutral.inkSoft)};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => (p.$active ? p.theme.fontWeight.semibold : p.theme.fontWeight.medium)};
  cursor: pointer;
  position: relative;
  justify-content: ${(p) => (p.$collapsed ? 'center' : 'flex-start')};
  text-align: start;
  text-decoration: none;
  box-sizing: border-box;
  &:hover {
    background: ${(p) =>
      p.$active ? p.theme.colors.brand.primarySoft : p.theme.colors.neutral.panelAlt};
  }
  ${focusRing}
  ${(p) =>
    p.$active &&
    !p.$collapsed &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        top: 6px;
        bottom: 6px;
        width: 2px;
        background: ${p.theme.colors.brand.primary};
        border-radius: 2px;
      }
    `}
`;

const Badge = styled.span<{ $tone?: string }>`
  padding: 1px 6px;
  border-radius: ${(p) => p.theme.radius.lg};
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  background: ${(p) =>
    p.$tone === 'error' ? p.theme.colors.semantic.error.soft : p.theme.colors.neutral.bg};
  color: ${(p) =>
    p.$tone === 'error' ? p.theme.colors.semantic.error.color : p.theme.colors.neutral.inkSoft};
  margin-inline-start: auto;
`;

/**
 * Sidebar navigation entry. Renders as an `<a>` by default — pass `as` to
 * render as a router `Link`, a `<button>`, or any other element/component.
 *
 * The library does not depend on a router; consumers wire `active` from
 * their own routing source.
 *
 * @example
 * <NavItem href="/schedule" icon={<Icon name="clock" />} label="Schedule" active />
 *
 * @example
 * // With React Router
 * <NavItem as={Link} to="/rides" icon={<Icon name="bus" />} label="Rides" />
 *
 * @example
 * // As a non-link action
 * <NavItem as="button" type="button" onClick={...}
 *   icon={<Icon name="logout" />} label="Sign out" />
 */
export function NavItem<E extends React.ElementType = 'a'>({
  as,
  icon,
  label,
  active,
  badge,
  badgeTone,
  collapsed,
  ...rest
}: NavItemProps<E>) {
  const Component = (as ?? 'a') as React.ElementType;
  return (
    <Root
      as={Component}
      $active={!!active}
      $collapsed={!!collapsed}
      aria-current={active ? 'page' : undefined}
      {...(rest as Record<string, unknown>)}
    >
      {icon}
      {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
      {!collapsed && badge && <Badge $tone={badgeTone}>{badge}</Badge>}
    </Root>
  );
}
